// https://blog.devgenius.io/how-to-better-poll-apis-in-react-312bddc604a4

import { useCallback, useEffect, useRef } from "react";

const useIntervalAsync = <R = unknown>(fn: () => Promise<R>, ms: number, enabled = true) => {
  const runningCount = useRef(0);
  const timeout = useRef<number>();
  const mountedRef = useRef(false);

  const next = useCallback((handler: TimerHandler) => {
    if (enabled && mountedRef.current && runningCount.current === 0) {
      timeout.current = setTimeout(handler, ms);
    }
  }, [ms, enabled]);

  const run = useCallback(async () => {
    runningCount.current += 1;
    const result = await fn();
    runningCount.current -= 1;
    next(run);
    return result;
  }, [fn, next]);

  useEffect(() => {
    mountedRef.current = true;
    next(run);
    return () => {
      mountedRef.current = false;
      clearTimeout(timeout.current);
    };
  }, [next, run]);

  const flush = useCallback(() => {
    clearTimeout(timeout.current);
    return run();
  }, [run]);

  return flush;
};

export default useIntervalAsync;
